import createApiService from "@lib/create-api-service";
import { generateHashMac } from "@lib/hashMacGenerator/generator";
import { generateNonce } from "@lib/nonceGenerator/generator";
import { HERE_MAPS_ACCESS_KEYS, HERE_MAPS_SECRET_KEYS } from "@lib/street-autocomplete/constants";
import { TOAuthResponseOriginal, TOAuthResponseTransformed } from "@lib/street-autocomplete/types";
import getEnv from "@utils/getEnv";

const getToken = createApiService<TOAuthResponseOriginal>({
  method: "POST",
  baseURL: getEnv("HERE_API_OAUTH_TOKEN_URL"),
  url: "/oauth2/token",
  isExternalApi: true,
});

export const loadOauthToken = async (
  defaultLocale: string
): Promise<{
  data: TOAuthResponseTransformed | undefined;
  error?: string | undefined;
}> => {
  const oauthConsumerKey = HERE_MAPS_ACCESS_KEYS[defaultLocale];
  const oauthNonce = generateNonce();
  const oauthTimeStamp = Math.round(new Date().getTime() / 1000);
  const oauthVersion = "1.0";
  const accessKeySecret = HERE_MAPS_SECRET_KEYS[defaultLocale];
  let parameterString = "grant_type=client_credentials";
  parameterString += `&oauth_consumer_key=${oauthConsumerKey}`;
  parameterString += `&oauth_nonce=${oauthNonce}`;
  parameterString += "&oauth_signature_method=HMAC-SHA256";
  parameterString += `&oauth_timestamp=${oauthTimeStamp}`;
  parameterString += `&oauth_version=${oauthVersion}`;
  const encodedParameterString = encodeURIComponent(parameterString);
  const encodedBaseString = `POST&${encodeURIComponent(
    `${getEnv("HERE_API_OAUTH_TOKEN_URL") || ""}/oauth2/token`
  )}&${encodedParameterString}`;
  const encodedSignature = encodeURIComponent(generateHashMac(`${accessKeySecret}&`, encodedBaseString));
  let header = `OAuth oauth_consumer_key="${oauthConsumerKey}"`;
  header += ',oauth_signature_method="HMAC-SHA256"';
  header += `,oauth_timestamp="${oauthTimeStamp}"`;
  header += `,oauth_nonce="${oauthNonce}"`;
  header += `,oauth_version="${oauthVersion}"`;
  header += `,oauth_signature="${encodedSignature}"`;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const params = new URLSearchParams({ grant_type: "client_credentials" });
  try {
    const oauthToken = await getToken({
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Authorization: header,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        "Content-Type": "application/x-www-form-urlencoded",
      } as any,
      data: params,
    });

    if (oauthToken.isSuccess) {
      const tokenData = oauthToken.data;

      if (!tokenData) {
        return { data: undefined, error: undefined };
      }

      return {
        data: {
          accessToken: tokenData.access_token,
          expiresIn: tokenData.expires_in,
          tokenType: tokenData.token_type,
        },
        error: undefined,
      };
    }

    return { data: undefined, error: oauthToken.errorMessage };
  } catch (err) {
    console.error(err);
    return { data: undefined, error: undefined };
  }
};
